/* eslint-disable max-len */
import {
    Badge,
    FormControl,
    FormHelperText,
    FormLabel,
    HStack,
    Switch,
    Text,
    VStack,
} from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';
import { NotificationPreferenceFormValues } from './form-schema';

const emailNotificationOptions = [
    {
        key: 'rmsWorkOrderReceivedEnabled' as const,
        label: 'Work order received',
        description: 'When we have accepted your Minimum standards work order for a property',
    },
    {
        key: 'rmsCheckScheduledEnabled' as const,
        label: 'Assessment booked',
        description: 'When the Minimum standards assessment has been booked in',
        isNew: true,
    },
    {
        key: 'rmsCheckCancelledEnabled' as const,
        label: 'Assessment booking cancelled',
        description: 'When a booked Minimum standards assessment has had to be rescheduled',
        isNew: true,
    },
];

export function RmsEmailNotificationPreferences({
    propertyManagerName,
}: {
    propertyManagerName?: string | null;
}) {
    const { control } = useFormContext<NotificationPreferenceFormValues>();
    const displayName = propertyManagerName?.trim() ?? 'property manager';

    return (
        <FormControl>
            <FormLabel htmlFor="none" fontWeight="bold">
                Minimum Standards (RMS) email notifications
            </FormLabel>
            <FormHelperText color="muted.500" w={['100%', null, '70%']} mb={4}>
                <Text lineHeight={1.3}>
                    Choose which emails
                    {' '}
                    {displayName}
                    {' '}
                    will receive about Minimum Standards assessments. Emails will go to the Property Manager who requests the work order.
                    <br />
                    <br />
                    You are unable to unsubscribe from emails about the result of a Minimum Standards assessment.
                </Text>
            </FormHelperText>

            <VStack align="stretch" spacing={4} w={['100%', null, '70%']}>
                {emailNotificationOptions.map((option) => (
                    <Controller
                        key={option.key}
                        name={option.key}
                        control={control}
                        render={({ field }) => (
                            <FormControl display="flex" alignItems="flex-start" justifyContent="space-between" gap={4}>
                                <FormLabel htmlFor={option.key} mb={0} fontWeight="medium">
                                    <HStack spacing={2} align="center">
                                        <Text fontSize="sm" fontWeight="bold">{option.label}</Text>
                                        {option.isNew === true && (
                                            <Badge colorScheme="info" size="sm">
                                                NEW
                                            </Badge>
                                        )}
                                    </HStack>
                                    <Text color="muted.500" fontSize="sm" fontWeight="normal">
                                        {option.description}
                                    </Text>
                                </FormLabel>
                                <Switch
                                    id={option.key}
                                    isChecked={field.value}
                                    onChange={(event) => field.onChange(event.target.checked)}
                                    mt={1}
                                />
                            </FormControl>
                        )}
                    />
                ))}
            </VStack>
        </FormControl>
    );
}
